import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { AmountUtils, cn, PaletteUtils } from '@shared/utils';
import { TransactionDirection } from '@shared/api';
import { Tooltip } from '../tooltip';
import { Icon, NoData, Text } from '@shared/widgets';
import TransactionIcon from '@assets/icons/dashboard/transaction.svg';

interface TransactionItemProps {
  amount: number;
  numberOfTransaction: number;
  highestTransferAmount: {
    date: string;
    amount: number;
  }[];
  recurrentRecipientsInfo: {
    recipientName: string;
    amount: number;
    numberOfTransaction: number;
  }[];
  textValue: string;
  currency: string;
  itemType: TransactionDirection;
}

export const TransactionItem = (props: TransactionItemProps) => {
  const { t } = useTranslation('dashboard');

  const highestElement = useMemo(
    () => props.highestTransferAmount?.slice()?.sort((a, b) => b.amount - a.amount)?.[0]?.amount,
    [props.highestTransferAmount],
  );
  return (
    <div className={cn('flex gap-6', 'max-xl:flex-wrap', PaletteUtils.isVcfcPalette && ['p-6', 'max-md:p-4'])}>
      <div
        className={cn(
          'relative w-full',
          PaletteUtils.isInsigniaPalette && 'bg-white pl-6 pt-6 dark:bg-soft-black',
          PaletteUtils.isVcfcPalette && 'bg-ghost-white p-6 dark:bg-light/5',
        )}
      >
        <div className='flex max-w-48 flex-col gap-1'>
          <Text className={cn(PaletteUtils.isVcfcPalette && 'dark:text-white')} size='3xl' color='primary'>
            {AmountUtils.transformToAmountWithCurrency(props.amount, props.currency)}
          </Text>
          <Text color='secondary'>
            {t('payment.transaction.amountDuringPeriod', {
              itemType: t(`payment.transaction.type.${props.itemType.toLowerCase()}`),
              period: props.textValue,
            })}
          </Text>
        </div>
        <div className='mt-6 flex max-w-60 flex-col gap-1'>
          <Text className={cn(PaletteUtils.isVcfcPalette && 'dark:text-white')} size='3xl' color='primary'>
            {props.numberOfTransaction}
          </Text>
          <Text color='secondary'>
            {t('payment.transaction.transactionsCountDuringPeriod', {
              itemType: t(`payment.transaction.type.${props.itemType.toLowerCase()}`).toLowerCase(),
              period: props.textValue,
            })}
          </Text>
        </div>
        {PaletteUtils.isInsigniaPalette && <Icon icon={<TransactionIcon />} className='flex w-full justify-end' color='primary' />}
      </div>
      <div
        className={cn(
          'flex w-full flex-col justify-between gap-3 p-6',
          PaletteUtils.isVcfcPalette && 'bg-ghost-white dark:bg-light/5',
          PaletteUtils.isInsigniaPalette && 'bg-white dark:bg-soft-black',
        )}
      >
        <Text weight='semibold'>
          {t('payment.transaction.top5DaysAmount', { itemType: t(`payment.transaction.type.${props.itemType.toLowerCase()}`).toLowerCase() })}
        </Text>
        {props.highestTransferAmount?.length > 0 ? (
          <div className='flex items-end justify-center gap-2'>
            {props.highestTransferAmount.map((item, idx) => (
              <Tooltip key={idx} content={AmountUtils.transformToAmountWithCurrency(item.amount, props.currency)} direction='right'>
                <div className='flex flex-col'>
                  <div
                    className={cn(
                      'max-h-[120px] w-12 rounded-md border',
                      PaletteUtils.isVcfcPalette && [
                        highestElement === item.amount
                          ? 'border-primary bg-primary dark:border-white dark:bg-white'
                          : 'border-primary dark:border-white',
                      ],
                    )}
                    style={{ height: `calc(120px * ${(item.amount / highestElement).toFixed(3)})` }}
                  />
                  <Text className={cn('mt-2 dark:text-white/50', PaletteUtils.isVcfcPalette && 'text-slate')} center>
                    {dayjs(item.date).format('DD.MM')}
                  </Text>
                </div>
              </Tooltip>
            ))}
          </div>
        ) : (
          <NoData className='m-auto' text={t('noData.noRecords', { ns: 'common' })} />
        )}
      </div>
      <div
        className={cn(
          'flex w-full flex-col gap-3 p-6',
          PaletteUtils.isInsigniaPalette && 'bg-white dark:bg-soft-black',
          PaletteUtils.isVcfcPalette && 'bg-ghost-white dark:bg-light/5',
        )}
      >
        <Text weight='semibold'>
          {t('payment.transaction.top5DaysRecurrent', {
            type: TransactionDirection.Outgoing === props.itemType ? t('payment.transaction.recipients') : t('payment.transaction.senders'),
          })}
        </Text>
        {props.recurrentRecipientsInfo?.length > 0 ? (
          <div className='flex flex-col gap-5'>
            {props.recurrentRecipientsInfo?.map((element, idx) => (
              <div key={idx} className='flex gap-2'>
                <div
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-full text-base font-semibold text-white',
                    PaletteUtils.isInsigniaPalette && 'bg-black',
                    PaletteUtils.isVcfcPalette && 'bg-primary',
                  )}
                >
                  {element.recipientName?.[0]}
                </div>
                <div>
                  <Text
                    className={cn(PaletteUtils.isVcfcPalette && 'dark:text-white')}
                    weight='semibold'
                    color={PaletteUtils.getColor(undefined, 'primary')}
                  >
                    {element.recipientName}
                  </Text>
                  <div className='flex items-center gap-2'>
                    <Text weight='semibold' size='xs' color='secondary' uppercase>
                      {AmountUtils.transformToAmountWithCurrency(element.amount, props.currency)}
                    </Text>
                    <Text weight='semibold' size='xs' color='secondary' uppercase>
                      {t('payment.transaction.transactionCount', { count: element.numberOfTransaction })}
                    </Text>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <NoData className='m-auto' text={t('noData.noRecords', { ns: 'common' })} />
        )}
      </div>
    </div>
  );
};
